import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Banknote,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
  ChevronDown,
  ChevronUp,
  Loader2,
  Coins,
} from "lucide-react";
import { useAuth } from "../../../hooks/useAuth";
import {
  useMyCashAdvances,
  useRequestCashAdvance,
} from "../../../hooks/useCashAdvance";
import { Modal } from "../../../components/ui/Modal";
import type { CashAdvance, CashAdvanceLimit } from "../../../lib/cashAdvanceApi";

type RequestForm = { amount: number; reason?: string };

const STATUS_CONFIG: Record<
  CashAdvance["status"],
  { label: string; badgeColor: string; borderColor: string; icon: typeof Clock }
> = {
  PENDING:  { label: "Pending",            badgeColor: "bg-[#fef9c3] text-[#a16207]",   borderColor: "border-l-[#f59e0b]", icon: Clock        },
  APPROVED: { label: "Approved",           badgeColor: "bg-[#dbeafe] text-[#1d4ed8]",   borderColor: "border-l-[#3b82f6]", icon: CheckCircle2 },
  RELEASED: { label: "Released — Repaying",badgeColor: "bg-[#ede9fe] text-[#6d28d9]",   borderColor: "border-l-[#7c3aed]", icon: Banknote     },
  PAID:     { label: "Settled",            badgeColor: "bg-[#dcfce7] text-[#15803d]",   borderColor: "border-l-[#16a34a]", icon: CheckCircle2 },
  REJECTED: { label: "Rejected",           badgeColor: "bg-[#fee2e2] text-[#b91c1c]",   borderColor: "border-l-[#dc2626]", icon: XCircle      },
};

function formatCurrency(n: number) {
  return `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ProgressBar({ repaid, total }: { repaid: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (repaid / total) * 100) : 0;
  return (
    <div className="w-full bg-[#e2e8f0] rounded-full h-1.5">
      <div
        className="bg-[#7c3aed] h-1.5 rounded-full transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function CashAdvanceCard({ ca }: { ca: CashAdvance }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[ca.status];
  const Icon = cfg.icon;

  return (
    <div className={`bg-white border border-[#e2e8f0] border-l-4 ${cfg.borderColor} rounded-xl overflow-hidden shadow-sm`}>
      <button
        type="button"
        className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-[#f8fafc] transition-colors"
        onClick={() => setExpanded((x) => !x)}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#f1f5f9]">
            <Icon className="h-4 w-4 text-[#64748b]" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[#0f172a] text-base">{formatCurrency(ca.amount)}</p>
            <p className="text-xs text-[#94a3b8] mt-0.5">
              Requested {format(new Date(ca.requestedAt), "MMM d, yyyy")}
            </p>
            {ca.status === "RELEASED" && (
              <div className="mt-2.5 space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-[#64748b]">
                  <span className="text-[#16a34a] font-medium">{formatCurrency(ca.repaidAmount)} repaid</span>
                  <span>·</span>
                  <span>{formatCurrency(ca.remainingAmount)} remaining</span>
                </div>
                <ProgressBar repaid={ca.repaidAmount} total={ca.amount} />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.badgeColor}`}>
            {cfg.label}
          </span>
          {expanded
            ? <ChevronUp className="h-4 w-4 text-[#94a3b8]" />
            : <ChevronDown className="h-4 w-4 text-[#94a3b8]" />
          }
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#f1f5f9] px-5 py-4 bg-[#f8fafc] space-y-2">
          {ca.reason && (
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-[#94a3b8]">Reason</span>
              <span className="text-[#374151] text-right">{ca.reason}</span>
            </div>
          )}
          {ca.approvedBy && (
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">Approved by</span>
              <span className="text-[#374151] font-medium">{ca.approvedBy}</span>
            </div>
          )}
          {ca.approvedAt && (
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">Approved on</span>
              <span className="text-[#374151]">{format(new Date(ca.approvedAt), "MMM d, yyyy")}</span>
            </div>
          )}
          {ca.releasedBy && (
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">Released by</span>
              <span className="text-[#374151] font-medium">{ca.releasedBy}</span>
            </div>
          )}
          {ca.releasedAt && (
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">Released on</span>
              <span className="text-[#374151]">{format(new Date(ca.releasedAt), "MMM d, yyyy")}</span>
            </div>
          )}
          {ca.releaseNote && (
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-[#94a3b8]">Note</span>
              <span className="text-[#374151] text-right">{ca.releaseNote}</span>
            </div>
          )}
          {ca.rejectedBy && (
            <div className="flex justify-between text-sm">
              <span className="text-[#94a3b8]">Rejected by</span>
              <span className="text-[#374151] font-medium">{ca.rejectedBy}</span>
            </div>
          )}
          {ca.rejectedReason && (
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-[#94a3b8]">Rejection reason</span>
              <span className="text-[#dc2626] text-right">{ca.rejectedReason}</span>
            </div>
          )}
          {ca.status === "RELEASED" && (
            <div className="pt-2.5 border-t border-[#e2e8f0]">
              <div className="flex justify-between text-sm font-medium mb-1.5">
                <span className="text-[#374151]">Repayment progress</span>
                <span className="text-[#7c3aed]">
                  {ca.amount > 0 ? Math.round((ca.repaidAmount / ca.amount) * 100) : 0}%
                </span>
              </div>
              <ProgressBar repaid={ca.repaidAmount} total={ca.amount} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Request Modal ─────────────────────────────────────────────────────────────

interface RequestModalProps {
  businessId: string;
  employeeName: string;
  limit: CashAdvanceLimit;
  onClose: () => void;
}

function RequestModalContent({ businessId, employeeName, limit, onClose }: RequestModalProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const requestMutation = useRequestCashAdvance();

  const requestSchema = useMemo(
    () =>
      z.object({
        amount: z
          .number({ invalid_type_error: "Amount is required" })
          .positive("Amount must be greater than 0")
          .max(
            limit.remainingCapacity > 0 ? limit.remainingCapacity : 9_999_999,
            limit.remainingCapacity > 0
              ? `Maximum allowed is ${formatCurrency(limit.remainingCapacity)}`
              : "Amount is too large",
          ),
        reason: z.string().max(500).optional(),
      }),
    [limit.remainingCapacity],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestForm>({
    resolver: zodResolver(requestSchema),
  });

  const onSubmit = (data: RequestForm) => {
    setServerError(null);
    requestMutation.mutate(
      { businessId, amount: data.amount, employeeName, reason: data.reason || undefined },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          const msg = (err as Error).message ?? "";
          if (msg.includes("SALARY_NOT_CONFIGURED")) {
            setServerError("Your profile does not have a salary configured. Please contact your admin.");
          } else if (msg.includes("EXCEEDS_CAPACITY") || msg.includes("EXCEEDS_SALARY")) {
            setServerError(`Requested amount exceeds your remaining capacity (${formatCurrency(limit.remainingCapacity)}).`);
          } else if (msg.includes("OUTSTANDING_ADVANCE_EXISTS")) {
            setServerError("You already have a pending or approved cash advance. Please wait for it to be processed.");
          } else {
            setServerError("Something went wrong. Please try again.");
          }
        },
      },
    );
  };

  const rows = [
    { label: "80% of bi-monthly salary", value: limit.biMonthlyCapacity, positive: true },
    { label: "Active loan installments",   value: limit.loanDeduction,      positive: false },
    { label: "Outstanding released CAs",   value: limit.outstandingCA,      positive: false },
  ];

  return (
    <div className="space-y-5">
      {/* Capacity breakdown */}
      <div className="rounded-xl bg-[#f8fafc] border border-[#e2e8f0] overflow-hidden">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between px-4 py-2.5 text-sm border-b border-[#f1f5f9] last:border-0">
            <span className="text-[#64748b]">{row.label}</span>
            <span className={row.positive ? "text-[#0f172a] font-medium" : "text-[#dc2626]"}>
              {row.positive ? "" : "− "}{formatCurrency(row.value)}
            </span>
          </div>
        ))}
        <div className="flex justify-between px-4 py-3 text-sm font-semibold bg-[#eff3ff] border-t border-[#e2e8f0]">
          <span className="text-[#374151]">You can request up to</span>
          <span className={limit.remainingCapacity > 0 ? "text-[#2447d9]" : "text-[#dc2626]"}>
            {formatCurrency(limit.remainingCapacity)}
          </span>
        </div>
      </div>

      <div className="rounded-lg bg-[#fffbeb] border border-[#fde68a] px-4 py-3 flex gap-2.5">
        <AlertTriangle className="h-4 w-4 text-[#d97706] mt-0.5 shrink-0" />
        <p className="text-xs text-[#92400e]">
          Cash advances are deducted from your upcoming payslip(s) until fully repaid.
          You cannot request a new advance while one is pending or awaiting approval.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input
              type="number"
              step="0.01"
              min="1"
              max={limit.remainingCapacity > 0 ? limit.remainingCapacity : undefined}
              className="w-full border border-[#e2e8f0] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[#0f172a] bg-[#f8fafc] focus:bg-white focus:ring-2 focus:ring-[#2447d9] focus:border-[#2447d9] outline-none transition-colors"
              placeholder={limit.remainingCapacity > 0 ? `Max ${formatCurrency(limit.remainingCapacity)}` : "e.g. 5000"}
              {...register("amount", { valueAsNumber: true })}
            />
          </div>
          {errors.amount && (
            <p className="mt-1 text-xs text-[#dc2626]">{errors.amount.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Reason <span className="text-[#94a3b8] font-normal">(optional)</span>
          </label>
          <textarea
            rows={3}
            maxLength={500}
            className="w-full border border-[#e2e8f0] rounded-lg px-3 py-2.5 text-sm text-[#0f172a] bg-[#f8fafc] focus:bg-white focus:ring-2 focus:ring-[#2447d9] focus:border-[#2447d9] outline-none resize-none transition-colors"
            placeholder="e.g. Medical emergency, school fees…"
            {...register("reason")}
          />
          {errors.reason && (
            <p className="mt-1 text-xs text-[#dc2626]">{errors.reason.message}</p>
          )}
        </div>

        {serverError && (
          <div className="rounded-lg bg-[#fef2f2] border border-[#fecaca] px-4 py-3 text-sm text-[#dc2626]">
            {serverError}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2.5 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={requestMutation.isPending || limit.remainingCapacity <= 0}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {requestMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit Request
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CashAdvancePage() {
  const { user, activeBusinessId } = useAuth();
  const [showModal, setShowModal] = useState(false);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyCashAdvances(undefined, activeBusinessId ?? undefined);
  const items = data?.pages.flatMap((p) => p.items) ?? [];

  const firstPage = data?.pages[0];
  const advanceLimit = {
    biMonthlyCapacity: firstPage?.biMonthlyCapacity ?? 0,
    loanDeduction:     firstPage?.loanDeduction ?? 0,
    outstandingCA:     firstPage?.outstandingCA ?? 0,
    remainingCapacity: firstPage?.remainingCapacity ?? 0,
  };

  const hasPendingOrApproved = items.some(
    (ca) => ca.status === "PENDING" || ca.status === "APPROVED",
  );

  const employeeName = user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
    : "";

  const activeItems  = items.filter((ca) => ca.status === "PENDING" || ca.status === "APPROVED" || ca.status === "RELEASED");
  const historyItems = items.filter((ca) => ca.status === "PAID" || ca.status === "REJECTED");

  const totalRepaid = historyItems
    .filter((ca) => ca.status === "PAID")
    .reduce((s, ca) => s + ca.amount, 0);

  return (
    <div className="space-y-5">

      {/* ── Summary bar ───────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#c7d2fe] bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
              <Coins className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#0f172a]">Cash Advance</h1>
              <p className="text-xs text-[#64748b]">Borrow against your upcoming payslip</p>
            </div>
          </div>

          {/* New Request — always visible, disabled when blocked */}
          <div className="shrink-0">
            <button
              onClick={() => !hasPendingOrApproved && setShowModal(true)}
              disabled={hasPendingOrApproved || isLoading}
              title={hasPendingOrApproved ? "You have a pending or approved advance" : undefined}
              className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Request
            </button>
            {hasPendingOrApproved && (
              <p className="text-[10px] text-[#6366f1] mt-1 text-right">Advance in progress</p>
            )}
          </div>
        </div>

        {/* Stat tiles */}
        {!isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-xl bg-white/60 px-3 py-2.5">
              <p className="text-[10px] font-semibold text-[#6366f1] uppercase tracking-wide mb-0.5">Can Request</p>
              <p className={`text-sm font-bold ${advanceLimit.remainingCapacity > 0 ? "text-[#2447d9]" : "text-[#dc2626]"}`}>
                {formatCurrency(advanceLimit.remainingCapacity)}
              </p>
              <p className="text-[10px] text-[#94a3b8] mt-0.5">Available capacity</p>
            </div>
            <div className="rounded-xl bg-white/60 px-3 py-2.5">
              <p className="text-[10px] font-semibold text-[#6366f1] uppercase tracking-wide mb-0.5">Active</p>
              <p className={`text-sm font-bold ${activeItems.length > 0 ? "text-[#7c3aed]" : "text-[#94a3b8]"}`}>
                {activeItems.length > 0 ? `${activeItems.length} · ${formatCurrency(activeItems.reduce((s, ca) => s + ca.remainingAmount, 0))}` : "None"}
              </p>
              <p className="text-[10px] text-[#94a3b8] mt-0.5">Outstanding</p>
            </div>
            <div className="col-span-2 sm:col-span-1 rounded-xl bg-white/60 px-3 py-2.5">
              <p className="text-[10px] font-semibold text-[#6366f1] uppercase tracking-wide mb-0.5">Total Settled</p>
              <p className="text-sm font-bold text-[#16a34a]">{formatCurrency(totalRepaid)}</p>
              <p className="text-[10px] text-[#94a3b8] mt-0.5">Fully repaid</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-[#2447d9]" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-sm text-[#dc2626]">
          Failed to load cash advances. Please try again.
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <Banknote className="h-7 w-7 text-[#2447d9]" />
          </div>
          <p className="font-medium text-[#0f172a]">No cash advances yet</p>
          <p className="text-sm text-[#94a3b8]">Tap "New Request" above to submit your first cash advance.</p>
        </div>
      ) : (
        <>
          {activeItems.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Active</p>
              {activeItems.map((ca) => (
                <CashAdvanceCard key={ca.cashAdvId} ca={ca} />
              ))}
            </section>
          )}

          {historyItems.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">History</p>
              {historyItems.map((ca) => (
                <CashAdvanceCard key={ca.cashAdvId} ca={ca} />
              ))}
            </section>
          )}

          {hasNextPage && (
            <div className="flex justify-center pt-1">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-lg border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Request Modal ──────────────────────────────────────────────────── */}
      {activeBusinessId && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Request Cash Advance"
          size="md"
        >
          <RequestModalContent
            businessId={activeBusinessId}
            employeeName={employeeName}
            limit={advanceLimit}
            onClose={() => setShowModal(false)}
          />
        </Modal>
      )}
    </div>
  );
}
